// Voice editor — "Apprendre de mes conversations" banner (US-096).
//
// Renders the gradient call-to-action banner the prototype ships at
// `prototypes_and_docs/ls-voice.jsx:877-914`. We gate visibility on
// `account.historyEligible`; the parent passes the live mutation handle
// from `useAnalyzeHistory()` so the CTA disables + swaps copy while the
// workflow is running. Wave 6 wires the actual workflow + WS progress.
//
// We use raw Tailwind utilities for the gradient surface because the DS
// Banner doesn't ship a gradient variant and the surface is bespoke to
// this banner per the prototype. `// DS-GAP: HistoryGradientBanner` is
// flagged in the agent report.

import { useT } from '@barel/i18n';
import { Button, FOCUS_RING_CLASS } from '@barel/ui';

export interface AnalyseHistoryBannerProps {
  historyConvs: number;
  busy: boolean;
  disabled?: boolean;
  onAnalyse: () => void;
}

export function AnalyseHistoryBanner({
  historyConvs,
  busy,
  disabled,
  onAnalyse,
}: AnalyseHistoryBannerProps) {
  const t = useT('voice');
  return (
    <div
      data-component="AnalyseHistoryBanner"
      className="flex items-center gap-3 rounded-xl border border-accent-border bg-accent-dim px-4 py-3"
    >
      <div
        className={
          'flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-accent text-on-accent text-base font-bold ' +
          FOCUS_RING_CLASS
        }
        aria-hidden
      >
        ✦
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-sm font-semibold text-primary">{t('history.bannerTitle')}</div>
        <p className="mt-0.5 text-xs text-secondary">
          {t('history.bannerBody', { count: historyConvs })}
        </p>
      </div>
      <Button
        variant="primary"
        size="sm"
        type="button"
        onClick={onAnalyse}
        loading={busy}
        disabled={disabled || busy}
        data-testid="voice-analyse-cta"
      >
        {busy ? t('history.bannerCtaBusy') : t('history.bannerCta')}
      </Button>
    </div>
  );
}
